import { useMemo } from "react";
import type { KioskCacheItem, LocationCacheItem } from "../../../types/session.ts";
import type { AdvancedFilterProps } from "./types.ts";
import { FreeTextIdentityFilter } from "./freeTextIdentityFilter.tsx";
import type { FreeTextIdentityConfig, GroupFieldDef } from "./freeTextIdentityFilter.tsx";
import { formatOperatorId, useOperatorNameById } from "./identityVariants/shared.tsx";
import { rankInHouseStatus, rollupInHouseStatus, type InHouseStatus } from "./inHouseStatus.ts";
import { useSiblingCheckbox } from "./filterSiblings.ts";

/** Aligned with KioskLocationTool's `isKioskLive`: LiveStatus = 1 is the
 *  reliable "currently live" marker. InHouseStatus / TestString are not
 *  reliable on production data (see kioskFilter.tsx for the same note). */
function isKioskLive(k: KioskCacheItem): boolean {
  return k.LiveStatus === 1;
}

export function LocationFilter({ info, isParameter, value, onChange, onSearch, cache }: AdvancedFilterProps) {
  const items = (cache.CommonData?.Location ?? []) as LocationCacheItem[];
  const kiosks = (cache.CommonData?.Kiosk ?? []) as KioskCacheItem[];

  // Kiosk-derived aggregates per location (Type, KioskDefined1-3). A location
  // may contain multiple kiosks with different values → each distinct value
  // places the location in that bucket. The location's own external-reference-
  // style field (StoreNumber) is read directly off the location row, not
  // aggregated from kiosks.
  const kioskAggByLocation = useMemo(() => {
    const m = new Map<number, { types: string[]; d1: string[]; d2: string[]; d3: string[]; kiosks: KioskCacheItem[] }>();
    for (const k of kiosks) {
      const locId = k.LocationID;
      let agg = m.get(locId);
      if (!agg) { agg = { types: [], d1: [], d2: [], d3: [], kiosks: [] }; m.set(locId, agg); }
      agg.kiosks.push(k);
      const kk = k as unknown as Record<string, unknown>;
      if (k.Type) agg.types.push(String(k.Type));
      if (kk.KioskDefined1) agg.d1.push(String(kk.KioskDefined1));
      if (kk.KioskDefined2) agg.d2.push(String(kk.KioskDefined2));
      if (kk.KioskDefined3) agg.d3.push(String(kk.KioskDefined3));
    }
    return m;
  }, [kiosks]);

  // Effective InHouseStatus per location = highest-priority status among its
  // kiosks (LIVE > … > ORDERED). Locations with no kiosks → TEST per product
  // rule. Computed from the kiosk cache rather than stored on the location
  // cache row to avoid a server-side roll-up step.
  const statusByLocation = useMemo(() => {
    const m = new Map<number, InHouseStatus>();
    for (const loc of items) {
      const ks = kioskAggByLocation.get(loc.LocationID)?.kiosks ?? [];
      m.set(loc.LocationID, rollupInHouseStatus(ks));
    }
    return m;
  }, [items, kioskAggByLocation]);

  // Sibling "Include Test Kiosks" checkbox. Hide a location only when EVERY
  // one of its kiosks is a test rig (legacy TestString or InHouseStatus=TEST)
  // AND it has at least one kiosk. Empty locations are not test rigs and stay.
  const includeTest = useSiblingCheckbox("Include Test Kiosks") ?? true;

  const visibleItems = useMemo(() => {
    if (includeTest) return items;
    return items.filter((loc) => {
      const ks = kioskAggByLocation.get(loc.LocationID)?.kiosks ?? [];
      if (ks.length === 0) return true;            // empty locations aren't "test rigs"
      return ks.some((k) => isKioskLive(k));       // keep if any child kiosk is live
    });
  }, [items, kioskAggByLocation, includeTest]);

  const sortedItems = useMemo(
    () => [...visibleItems].sort((a, b) => {
      const r = rankInHouseStatus(statusByLocation.get(a.LocationID))
              - rankInHouseStatus(statusByLocation.get(b.LocationID));
      return r !== 0 ? r : a.LocationID - b.LocationID;
    }),
    [visibleItems, statusByLocation],
  );

  function templateFn(item: LocationCacheItem): string {
    let s = String(item.LocationID);
    if (item.StoreName) s += ", " + item.StoreName;
    if (item.Address) s += ", " + item.Address;
    if (item.City) s += ", " + item.City;
    if (item.District) s += ", " + item.District;
    if (item.StoreNumber) s += " (" + item.StoreNumber + ")";
    return s;
  }

  function matchFn(item: LocationCacheItem, text: string): boolean {
    const lc = text.toLowerCase();
    return (
      item.LocationID.toString().indexOf(lc) === 0 ||
      (!!item.StoreName && item.StoreName.toLowerCase().indexOf(lc) >= 0) ||
      (!!item.Address && item.Address.toLowerCase().indexOf(lc) >= 0) ||
      (!!item.City && item.City.toLowerCase().indexOf(lc) >= 0) ||
      (!!item.District && item.District.toLowerCase().indexOf(lc) >= 0) ||
      (!!item.StoreNumber && item.StoreNumber.toLowerCase().indexOf(lc) >= 0)
    );
  }

  const operatorNameById = useOperatorNameById(cache.CommonData?.Common);

  /**
   * Group options, matching the legacy client's list. Location-native fields
   * (Address, City, Chain ≡ Retail Host, Country, County, Suburb, etc.) read
   * straight from the record; Kiosk-derived fields (Type, Kiosk Group #1-3)
   * aggregate across the location's kiosks.
   */
  const groupFields = useMemo<GroupFieldDef[]>(() => [
    { label: "Address", field: "Address" },
    { label: "Retail Host", field: "Chain" },
    { label: "City", field: "City" },
    { label: "County", field: "County" },
    { label: "State", field: "District" },
    {
      label: "Kiosk Group #1",
      field: "KioskDefined1_agg",
      getValue: (item) => kioskAggByLocation.get((item as LocationCacheItem).LocationID)?.d1 ?? [],
    },
    {
      label: "Kiosk Group #2",
      field: "KioskDefined2_agg",
      getValue: (item) => kioskAggByLocation.get((item as LocationCacheItem).LocationID)?.d2 ?? [],
    },
    {
      label: "Kiosk Group #3",
      field: "KioskDefined3_agg",
      getValue: (item) => kioskAggByLocation.get((item as LocationCacheItem).LocationID)?.d3 ?? [],
    },
    {
      label: "Location Group #1",
      field: "LocationDefined1",
      getValue: (item) => String((item as Record<string, unknown>).LocationDefined1 ?? ""),
    },
    {
      label: "Location Group #2",
      field: "LocationDefined2",
      getValue: (item) => String((item as Record<string, unknown>).LocationDefined2 ?? ""),
    },
    {
      label: "Location Group #3",
      field: "LocationDefined3",
      getValue: (item) => String((item as Record<string, unknown>).LocationDefined3 ?? ""),
    },
    { label: "Store Name", field: "StoreName" },
    { label: "Suburb", field: "Suburb" },
    {
      label: "Type",
      field: "KioskType_agg",
      getValue: (item) => kioskAggByLocation.get((item as LocationCacheItem).LocationID)?.types ?? [],
    },
    {
      label: "InHouseStatus",
      field: "InHouseStatus",
      getValue: (item) => statusByLocation.get((item as LocationCacheItem).LocationID) ?? "TEST",
    },
    { label: "Zip", field: "ZipCode" },
    { label: "Country", field: "Country" },
    {
      label: "Owner/Operator ID",
      field: "OwnerOperatorID",
      getValue: (item) => formatOperatorId((item as LocationCacheItem).OwnerOperatorID, operatorNameById),
    },
  ], [kioskAggByLocation, statusByLocation, operatorNameById]);

  const config = useMemo<FreeTextIdentityConfig<LocationCacheItem>>(
    () => ({
      idField: "LocationID",
      nameField: "StoreName",
      items: sortedItems,
      matchFn,
      templateFn,
      groupFields,
      getIdFromItem: (item) => item.LocationID,
      extraMeta: (item) => (item.StoreNumber ? item.StoreNumber : undefined),
    }),
    [sortedItems, groupFields],
  );

  return (
    <FreeTextIdentityFilter
      config={config}
      columnName={info.ColumnName}
      isParameter={isParameter}
      value={value}
      onChange={onChange}
      onSearch={onSearch}
    />
  );
}
